// ── lib/supabase.js ──
// Single shared SupabaseClient. Created once at module load; the client
// is safe to share across the entire app (auth state is stored in the
// client's internal storage adapter — localStorage by default).
//
// Env vars (Vercel project + .env.local):
//   VITE_SUPABASE_URL      — e.g. https://abcdefghij.supabase.co
//   VITE_SUPABASE_ANON_KEY — public "anon" key (safe to ship; protected by RLS)
//
// The service-role key NEVER lands in this file. Server-side helpers run
// only in supabase/functions/* (Deno).

import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Build-time guard: throw a clear error if anyone imports the client
// before env vars are wired. Never silently fall back to a no-op client —
// that ships a broken app to prod.
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error(
    "lib/supabase.js: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set. " +
    "Configure them in .env.local for local dev and in Vercel project env for deploys."
  );
}

// An invite or password-reset link lands here with the session in the URL
// hash (#access_token=…&type=invite). The client consumes and clears the
// hash on load, so note the link type first: the app then asks the person
// to choose a password before anything else.
export const AUTH_LINK_TYPE = (() => {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const type = p.get("type");
  return p.get("access_token") && (type === "invite" || type === "recovery") ? type : null;
})();

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,           // localStorage-backed session
    autoRefreshToken: true,
    detectSessionInUrl: true,       // OAuth redirect support (no-op for password auth)
  },
});

// Re-export for callers that want to introspect.
export const SUPABASE_PROJECT_URL = SUPABASE_URL;
